import cx from 'classnames';
import { BackButton } from 'components/admin/ui';
import { Modal } from 'components/ui/Modal';
import { XClose } from 'ethena-icons';
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion';
import _ from 'lodash';
import useTranslation from 'next-translate/useTranslation';
import {
  createContext,
  ElementType,
  forwardRef,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { Modal as BootstrapModal, Spinner } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { Card } from './Card';
import { ConfirmationModal } from './ConfirmationModal';
import { Navigation } from './Navigation';
import css from './SidePanel.module.scss';

enum NavigationAction {
  PUSH = 'PUSH',
  REPLACE = 'REPLACE',
  BACK = 'BACK',
}

enum ViewStatus {
  CURRENT = 'CURRENT',
  PREVIOUS = 'PREVIOUS',
  HIDDEN = 'HIDDEN',
}

export type ViewId = string | number;

const createAnimationVariant = (
  viewStatus: ViewStatus,
  action?: NavigationAction
) => (action ? `${viewStatus}_${action}` : viewStatus);

/**
 * Context
 */
export interface ISidePanelContext {
  /**
   * Trigger a backwards transition to a specified view. If no view is specified, the previous view will be used.
   * **However, prefer passing a view ID to ensure the correct view is navigated to, since we only keep track of the last view.**
   **/
  back: (id?: ViewId) => void;
  currentViewId: ViewId;
  initialViewId: ViewId;
  lastAction: NavigationAction;
  onClose: () => void;
  previousViewId: ViewId;
  push: (id: ViewId) => void;
  replace: (id: ViewId) => void;
}

const SidePanelContext = createContext<ISidePanelContext>({
  back: _.noop,
  currentViewId: undefined,
  initialViewId: undefined,
  lastAction: undefined,
  onClose: _.noop,
  previousViewId: undefined,
  push: _.noop,
  replace: _.noop,
});

interface ISidePanelViewContext {
  isCurrentView: boolean;
  isInitialView: boolean;
  viewStatus: ViewStatus;
}

// Default values are used to prevent issues when a `View` component is not used (e.g. a `Body` and `Footer` rendered directly in a `SidePanel`.).
const SidePanelViewContext = createContext<ISidePanelViewContext>({
  isCurrentView: true,
  isInitialView: true,
  viewStatus: ViewStatus.CURRENT,
});

export const useSidePanelContext = () => useContext(SidePanelContext);
const useSidePanelViewContext = () => useContext(SidePanelViewContext);

/**
 * Components
 */
export interface ISidePanelProps {
  backdrop?: boolean | 'static';
  backdropClassName?: string;
  children: ReactElement<IViewProps> | ReactElement<IViewProps>[];
  className?: string;
  initialViewId?: ViewId;
  isLoading?: boolean;
  isLoadingWithOverlay?: boolean;
  isVisible: boolean;
  onClose: () => void;
}

const SidePanel = ({
  backdrop,
  backdropClassName,
  children,
  className,
  initialViewId,
  isLoading,
  isLoadingWithOverlay,
  isVisible,
  onClose,
  ...rest
}: ISidePanelProps) => {
  const [currentViewId, setCurrentViewId] = useState<ViewId>(initialViewId);
  const [lastAction, setLastAction] = useState<NavigationAction>();
  const [previousViewId, setPreviousViewId] = useState<ViewId>();

  const back = (id?: ViewId) => {
    setLastAction(NavigationAction.BACK);
    setPreviousViewId(currentViewId);
    setCurrentViewId(id || previousViewId);
  };

  const push = (id: ViewId) => {
    setLastAction(NavigationAction.PUSH);
    setPreviousViewId(currentViewId);
    setCurrentViewId(id);
  };

  const replace = (id: ViewId) => {
    setLastAction(NavigationAction.REPLACE);
    setPreviousViewId(currentViewId);
    setCurrentViewId(id);
  };

  const reset = () => {
    setLastAction(undefined);
    setPreviousViewId(undefined);
    setCurrentViewId(initialViewId);
  };

  useEffect(() => {
    setCurrentViewId(initialViewId);
  }, [initialViewId]);

  const renderSpinner = () => (
    <Spinner animation="border" role="status">
      <span className="sr-only">Loading...</span>
    </Spinner>
  );

  return (
    <Modal
      backdrop={backdrop}
      backdropClassName={backdropClassName}
      className={cx(css.sidePanel, className)}
      onExited={reset}
      onHide={onClose}
      show={isVisible}
      {...rest}
    >
      <SidePanelContext.Provider
        value={{
          back,
          currentViewId,
          initialViewId,
          lastAction,
          onClose,
          previousViewId,
          push,
          replace,
        }}
      >
        {isLoading ? (
          <div className="d-flex align-items-center justify-content-center h-100">
            {renderSpinner()}
          </div>
        ) : (
          <>
            {isLoadingWithOverlay && (
              <div className={css.loadingOverlay}>{renderSpinner()}</div>
            )}
            {children}
          </>
        )}
      </SidePanelContext.Provider>
    </Modal>
  );
};

interface IViewProps {
  as?: ElementType;
  children?: ReactNode | ((props: ISidePanelContext) => ReactElement);
  className?: string;
  id: ViewId;
  /**
   * If `false`, the view children will not be rendered. Use this if you want to
   * defer rendering a view (e.g. data required to render the component has not
   * loaded yet). Defaults to `true`.
   */
  isRendered?: boolean;
  /**
   * Should the view be completely torn down (resetting all state) when
   * navigating away? Defaults to `false`.
   */
  isResetOnNavigate?: boolean;
  onBackClick?: (props: ISidePanelContext) => void;
  title: ReactNode;
}

const View = ({
  children,
  id,
  isRendered = true,
  isResetOnNavigate,
  onBackClick,
  title,
}: IViewProps) => {
  const context = useSidePanelContext();
  const { back, currentViewId, initialViewId, lastAction, previousViewId } =
    context;

  const getViewStatus = () => {
    if (!id || currentViewId === id) {
      return ViewStatus.CURRENT;
    } else if (previousViewId === id) {
      return ViewStatus.PREVIOUS;
    } else {
      return ViewStatus.HIDDEN;
    }
  };

  const viewStatus = getViewStatus();
  const isInitialView = !id || id === initialViewId;
  const isCurrentView = viewStatus === ViewStatus.CURRENT;
  const variant = createAnimationVariant(viewStatus, lastAction);

  const handleBackClick = () => (onBackClick ? onBackClick(context) : back());

  const renderChildren = () => {
    if ((isResetOnNavigate && !isCurrentView) || !isRendered) {
      return null;
    }
    return _.isFunction(children) ? children(context) : children;
  };

  return (
    <>
      {isCurrentView && (
        <Header onBackClick={isInitialView ? undefined : handleBackClick}>
          <Title>{title}</Title>
        </Header>
      )}
      <motion.div
        className={cx(css.view, { [css.current]: isCurrentView })}
        initial={ViewStatus.HIDDEN}
        animate={variant}
      >
        <AnimatePresence>
          <SidePanelViewContext.Provider
            value={{ isCurrentView, isInitialView, viewStatus }}
          >
            {renderChildren()}
          </SidePanelViewContext.Provider>
        </AnimatePresence>
      </motion.div>
    </>
  );
};

interface IHeaderProps extends HTMLAttributes<HTMLDivElement> {
  onBackClick?: () => void;
}

const Header = ({ children, className, onBackClick }: IHeaderProps) => {
  const { t } = useTranslation('modals');
  const { onClose } = useSidePanelContext();
  return (
    <BootstrapModal.Header className={className}>
      {!!onBackClick && <BackButton onClick={onBackClick} />}
      {children}
      <button
        aria-label={t('close')}
        className={css.closeButton}
        onClick={onClose}
      >
        <XClose height={20} width={20} />
      </button>
    </BootstrapModal.Header>
  );
};

const Title = ({ className, ...rest }: HTMLAttributes<HTMLHeadingElement>) => (
  <h1 className={cx(css.title, className)} {...rest} />
);

const BackdropOverlay = ({ ...rest }: HTMLAttributes<HTMLDivElement>) => {
  const { isCurrentView } = useSidePanelViewContext();
  return createPortal(
    isCurrentView && <div className={css.backdropOverlay} {...rest} />,
    document.body
  );
};

const bodyVariants = {
  [createAnimationVariant(ViewStatus.HIDDEN)]: {
    opacity: 0,
    transform: 'translateX(0)',
    zIndex: 'unset',
  },
  [createAnimationVariant(ViewStatus.CURRENT)]: {
    opacity: 1,
    transform: 'translateX(0)',
    zIndex: 1,
  },
  [createAnimationVariant(ViewStatus.CURRENT, NavigationAction.BACK)]: {
    opacity: [0, 1],
    transform: ['translateX(-10%)', 'translateX(0)'],
    zIndex: ['unset', 1],
  },
  [createAnimationVariant(ViewStatus.CURRENT, NavigationAction.PUSH)]: {
    opacity: [0, 1],
    transform: ['translateX(10%)', 'translateX(0)'],
    zIndex: ['unset', 1],
  },
  [createAnimationVariant(ViewStatus.CURRENT, NavigationAction.REPLACE)]: {
    opacity: [0, 1],
    transform: 'translateX(0)',
    zIndex: ['unset', 1],
  },
  [createAnimationVariant(ViewStatus.PREVIOUS, NavigationAction.BACK)]: {
    opacity: [1, 0],
    transform: ['translateX(0)', 'translateX(10%)'],
    zIndex: [1, 'unset'],
  },
  [createAnimationVariant(ViewStatus.PREVIOUS, NavigationAction.PUSH)]: {
    opacity: [1, 0],
    transform: ['translateX(0)', 'translateX(-10%)'],
    zIndex: [1, 'unset'],
  },
  [createAnimationVariant(ViewStatus.PREVIOUS, NavigationAction.REPLACE)]: {
    opacity: [1, 0],
    transform: 'translateX(0)',
    zIndex: [1, 'unset'],
  },
};

interface IBodyProps extends Omit<HTMLMotionProps<'div'>, 'children'> {
  children?: ReactNode;
}

const Body = forwardRef<HTMLDivElement, IBodyProps>(
  ({ children, className, ...rest }, ref) => (
    <Modal.Body
      as={motion.div}
      className={className}
      ref={ref}
      {...rest}
      transition={{ ease: 'easeInOut', duration: 0.15 }}
      variants={bodyVariants}
    >
      {children}
    </Modal.Body>
  )
);

Body.displayName = 'Body';

const DetailsList = (props: HTMLAttributes<HTMLDListElement>) => (
  <dl className={css.detailsList} {...props} />
);

const Footer = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ children, className, ...rest }, ref) => {
    const { isCurrentView } = useSidePanelViewContext();
    return (
      isCurrentView && (
        <div className={cx(css.footer, className)} ref={ref} {...rest}>
          {children}
        </div>
      )
    );
  }
);

Footer.displayName = 'Footer';

SidePanel.BackdropOverlay = BackdropOverlay;
SidePanel.Body = Body;
SidePanel.Card = Card;
SidePanel.ConfirmationModal = ConfirmationModal;
SidePanel.DetailsList = DetailsList;
SidePanel.Dialog = BootstrapModal.Dialog;
SidePanel.Footer = Footer;
SidePanel.Header = Header;
SidePanel.Navigation = Navigation;
SidePanel.Title = Title;
SidePanel.View = View;

export { SidePanel };
